import { useEffect, useCallback } from 'react'
import * as d3 from 'd3'
import accounting from 'accounting'
import classnames from 'classnames'
import { find, isEmpty, isFinite } from 'lodash'
import { DateTime } from 'luxon'

import { DataPoint } from './index'
import { DateRange } from '../../../../../typings/dates'

import styles from './styles.module.css'

export interface GraphDimensions {
  height?: number
  width?: number
}

export const usePerformanceGraph = (
  elementId: string,
  dimensions: GraphDimensions,
  displayType: string,
  dateRange: DateRange,
  graphData?: DataPoint[]
) => {
  const renderGraph = useCallback(
    (
      elementId: string,
      dimensions: GraphDimensions,
      displayType: string,
      dateRange: DateRange,
      graphData?: DataPoint[]
    ) => {
      const margin = { top: 25, right: 56, bottom: 26, left: 27 }
      const graphWidth = dimensions.width - margin.left - margin.right
      const graphHeight = dimensions.height - margin.top - margin.bottom

      if (!isEmpty(graphData)) {
        let data

        // Generate array of days between the chosen date range
        const parseTime = d3.timeParse('%Y-%m-%d')
        const generatedDateRange = d3.timeDays(
          parseTime(dateRange.startDate),
          d3.timeDay.offset(parseTime(dateRange.endDate), 1)
        )

        if (!isEmpty(graphData)) {
          data = generatedDateRange.map(d => {
            const formattedDate = DateTime.fromJSDate(d).toISODate()

            const datapoint = find(graphData, ['date', formattedDate])

            return {
              date: d,
              value:
                datapoint && isFinite(datapoint.value) ? datapoint.value : null,
            }
          })
        } else {
          data = generatedDateRange.map(date => ({
            date,
            value: null,
          }))
        }

        // set the ranges
        const x = d3.scaleTime().range([0, graphWidth])
        const y = d3.scaleLinear().range([graphHeight, 0])

        // define the axis scale
        const xDomain = d3.extent(data, d => d.date)
        const yMax = d3.max(data, d => d.value)
        const yDomain = [0, yMax > 0 ? yMax : 1]

        x.domain(xDomain)
        y.domain(yDomain)

        // define the X axis
        const xAxis = d3.axisBottom(x).tickFormat(d =>
          d3
            .timeFormat('%b %e')(d)
            .toUpperCase()
        )

        const customXAxis = g => {
          g.call(xAxis)
          g.select('.domain').remove()
          g.selectAll('.tick line').attr('class', styles.gridlines)
          g.selectAll('.tick text')
            .attr('font-weight', '600')
            .attr('font-size', '10px')
            .attr('transform', 'translate(0, 1)')
        }

        // define the Y axis
        const valueFormatter = (value, type) => {
          if (type === 'commissions') {
            return accounting.formatMoney(value, '$', 0)
          }

          return value
        }

        const yAxis = d3
          .axisRight(y)
          .tickSize(margin.right)
          .tickFormat(d => valueFormatter(d, displayType))

        const customYAxis = g => {
          g.call(yAxis)
          g.select('.domain').remove()
          g.selectAll('.tick line').attr('class', styles.gridlines)
          g.selectAll('.tick text')
            .attr('font-weight', '600')
            .attr('font-size', '10px')
            .attr('x', 4)
            .attr('dy', -4)
        }

        // define the graph area under the value line
        const area = d3
          .area()
          .defined(d => d.value !== null)
          .x(d => x(d.date) + 1)
          .y0(graphHeight + 1)
          .y1(d => y(d.value))

        // define the Y gridlines
        const leftYGridlines = d3
          .axisRight(y)
          .tickSize(-margin.left)
          .tickFormat('')

        const rightYGridlines = d3
          .axisLeft(y)
          .tickSize(-graphWidth)
          .tickFormat('')

        // This covers the area where the axis labels usually go.
        // There might be a better way to handle this.
        const customLeftYGridlines = g => {
          g.call(leftYGridlines)
          g.select('.domain').remove()
          g.selectAll('.tick line').attr('class', styles.gridlines)
        }

        const customRightYGridlines = g => {
          g.call(rightYGridlines)
          g.select('.domain').remove()
          g.selectAll('.tick line').attr('class', styles.gridlines)
        }

        // define the value line
        const lineGenerator = d3
          .line()
          .defined(d => d.value !== null)
          .x(d => x(d.date))
          .y(d => y(d.value))

        // clear the graph area
        d3.select(`#${elementId}`)
          .selectAll('*')
          .remove()

        // add graph to the page
        const graph = d3
          .select(`#${elementId}`)
          .append('svg')
          .attr('width', graphWidth + margin.left + margin.right)
          .attr('height', graphHeight + margin.top + margin.bottom)
          .append('g')
          .attr('transform', `translate(${margin.left}, ${margin.top})`)

        // add the X axis
        graph
          .append('g')
          .attr('transform', `translate(0, ${graphHeight})`)
          .call(customXAxis)

        // add the Y axis
        graph
          .append('g')
          .attr('transform', `translate(${graphWidth}, 0)`)
          .call(customYAxis)

        // add the area
        graph
          .append('path')
          .data([data])
          .attr('class', styles.area)
          .attr('d', area)

        // add the Y gridlines
        graph
          .append('g')
          .attr('class', 'left-grid')
          .call(customLeftYGridlines)

        graph
          .append('g')
          .attr('class', 'grid')
          .call(customRightYGridlines)

        // add the line
        graph
          .append('path')
          .data([data])
          .attr('class', styles.line)
          .attr('d', lineGenerator)

        // add the intersections
        graph
          .selectAll(styles.dot)
          .data(data.filter(d => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', styles.dot)
          .attr('cx', d => x(d.date))
          .attr('cy', d => y(d.value))
          .attr('r', 3)

        // define the area gradient
        const defs = graph.append('defs')
        const gradient = defs
          .append('linearGradient')
          .attr('id', 'svgGradient')
          .attr('x1', '0%')
          .attr('x2', '100%')

        gradient
          .append('stop')
          .attr('class', 'start')
          .attr('offset', '0%')
          .attr('stop-color', '#F1FCFF')
          .attr('stop-opacity', 1)

        gradient
          .append('stop')
          .attr('class', 'middle')
          .attr('offset', '50%')
          .attr('stop-color', '#BEE5EF')
          .attr('stop-opacity', 1)

        gradient
          .append('stop')
          .attr('class', 'end')
          .attr('offset', '100%')
          .attr('stop-color', '#93BCD1')
          .attr('stop-opacity', 1)

        // add the crosshairs
        const target = graph.append('g').style('display', 'none')
        target
          .append('line')
          .attr('id', 'focusLineX')
          .attr('class', styles.focusLine)
        target
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 50)
          .attr('height', 20)
          .attr('rx', 5)
          .attr('ry', 5)
          .attr('id', 'xIndicator')
          .attr('class', styles.axisIndicator)
        target
          .append('text')
          .text('')
          .attr('text-anchor', 'middle')
          .attr('id', 'xLabel')
          .attr('class', styles.axisLabel)
        target
          .append('line')
          .attr('id', 'focusLineY')
          .attr('class', styles.focusLine)
        target
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 50)
          .attr('height', 20)
          .attr('rx', 5)
          .attr('ry', 5)
          .attr('id', 'yIndicator')
          .attr('class', styles.axisIndicator)
        target
          .append('text')
          .text('')
          .attr('text-anchor', 'middle')
          .attr('id', 'yLabel')
          .attr('class', styles.axisLabel)
        target
          .append('circle')
          .attr('id', 'focusCircle')
          .attr('r', 4)
          .attr('class', classnames(styles.dot, styles.focusCircle))
        target
          .append('circle')
          .attr('id', 'focusHalo')
          .attr('r', 8)
          .attr('class', classnames(styles.dot, styles.focusHalo))

        // focus tracking
        const bisectDate = d3.bisector(d => d.date).left
        graph
          .append('rect')
          .attr('class', styles.overlay)
          .attr('width', graphWidth + margin.right)
          .attr('height', graphHeight + margin.bottom)
          .on('mouseout', () => {
            target.style('display', 'none')
          })
          .on('mousemove', (targetData, targetIndex, nodes) => {
            const mouse = d3.mouse(nodes[targetIndex])
            const mouseDate = x.invert(mouse[0])

            const xAxisIndex = bisectDate(data, mouseDate, 1)

            const d0 = data[xAxisIndex - 1]
            const d1 = data[xAxisIndex]

            let focusPoint =
              typeof d1 !== 'undefined' &&
              mouseDate - d0.date >= d1.date - mouseDate
                ? d1
                : d0

            if (focusPoint && focusPoint.value !== null) {
              const targetX = x(focusPoint.date)
              const targetY = y(focusPoint.value)

              target.style('display', null)
              target
                .select('#focusHalo')
                .attr('cx', targetX)
                .attr('cy', targetY)
              target
                .select('#focusCircle')
                .attr('cx', targetX)
                .attr('cy', targetY)
              target
                .select('#focusLineX')
                .attr('x1', targetX)
                .attr('y1', y(yDomain[0]) + 4)
                .attr('x2', targetX)
                .attr('y2', y(yDomain[1]) - margin.top)
              target
                .select('#xIndicator')
                .attr('x', targetX - 25)
                .attr('y', graphHeight + 4)
              target
                .select('#xLabel')
                .attr('x', targetX)
                .attr('y', graphHeight + 17)
                .text(
                  d3
                    .timeFormat('%b %e')(focusPoint.date)
                    .toUpperCase()
                )
              target
                .select('#focusLineY')
                .attr('x1', x(xDomain[0]) - margin.left)
                .attr('y1', targetY)
                .attr('x2', x(xDomain[1]) + 4)
                .attr('y2', targetY)
              target
                .select('#yIndicator')
                .attr('x', graphWidth + 4)
                .attr('y', targetY - 12)
              target
                .select('#yLabel')
                .attr('x', graphWidth + margin.right / 2)
                .attr('y', targetY + 1)
                .text(valueFormatter(focusPoint.value, displayType))
            }
          })
      } else {
        d3.select(`#${elementId}`)
          .selectAll('*')
          .remove()

        const graph = d3
          .select(`#${elementId}`)
          .append('svg')
          .attr('width', dimensions.width)
          .attr('height', dimensions.height)

        graph
          .append('text')
          .attr('class', styles.box)
          .attr('x', dimensions.width / 2)
          .attr('y', dimensions.height / 2)
          .attr('text-anchor', 'middle')
          .text('No data is available to display.')
      }
    },
    []
  )

  useEffect(() => {
    renderGraph(elementId, dimensions, displayType, dateRange, graphData)
  }, [dateRange, dimensions, displayType, elementId, graphData, renderGraph])
}
